"use client";

import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { me } from '@/src/api/user'
import AdminToolbar from '@/src/components/layout/AdminToolbar'
import { Button } from '@/src/components/ui/button'
import { DataTable, type ColumnConfig } from '@/src/components/manage/tables/DataTable'
import { ListIcon, TrashIcon, WrenchIcon } from '@phosphor-icons/react'
import { CreateAction } from '@/src/components/manage/actions/CreateAction'
import { Badge } from '@/src/components/ui/badge'
import { EnabledAction } from '@/src/components/manage/actions/EnabledAction'
import { EditAction } from '@/src/components/manage/actions/EditAction'
import { ViewAction } from '@/src/components/manage/actions/ViewAction'
import { CloneAction } from '@/src/components/manage/actions/CloneAction'
import { DeleteAction } from '@/src/components/manage/actions/DeleteAction'
import { Spinner } from '@/src/components/ui/spinner'
import { useTranslation } from '@/src/i18n/client'
import { userCanManageOrganizationSettings } from '@/src/lib/permissionHelpers'
import {
  listOrgOperationTypes,
  listGlobalOperationTypes,
  deleteOperationType,
  cloneOperationType,
  updateOperationType,
} from '@/src/api/operation-type'
import { toast } from 'sonner'
import { ConfirmModal } from '@/src/components/ui/confirm-modal'
import { ToolbarTabLink } from '@/src/components/layout/ToolbarTabToggle'
import type { FormSchemaDefinition, LocalizedString } from '@/shared/schemas/operation-data.zod'

export const Route = createFileRoute('/_manage/manage/organization/operation-types')({
  component: OrgOperationTypesPage,
})

interface OperationTypeRow {
  id: string
  name: string
  labels: LocalizedString
  description?: LocalizedString | null
  icon?: string | null
  version: number
  formSchema: FormSchemaDefinition
  organizationId?: string | null
  clonedFromId?: string | null
  clonedFrom?: { id: string; name: string; labels: LocalizedString } | null
  enabled: boolean
  deletedAt?: string | null
  createdAt: string
  updatedAt: string
  _count?: {
    operations: number
    clones?: number
  }
}

function OrgOperationTypesPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [selectedItems, setSelectedItems] = useState<string[]>([])
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false)

  // Get current user
  const { data: user, isLoading: isUserLoading } = useQuery({
    queryKey: ['user', 'me'],
    queryFn: () => me(),
  })

  const permissions: string[] = (user?.role?.permissions as string[]) ?? []
  const isAdmin = permissions.includes('*')
  const canManageOrg = userCanManageOrganizationSettings(permissions, isAdmin)
  const orgId = user?.organization?.id

  // Get org-specific types
  const { data: orgTypes = [], isLoading: isOrgTypesLoading } = useQuery({
    queryKey: ['operationTypes', 'org', orgId],
    queryFn: () => listOrgOperationTypes({ data: { includeDisabled: true } }),
    enabled: canManageOrg && !!orgId,
  })

  // Get global types (for cloning)
  const { data: globalTypes = [], isLoading: isGlobalTypesLoading } = useQuery({
    queryKey: ['operationTypes', 'global'],
    queryFn: () => listGlobalOperationTypes({ data: { includeDisabled: false } }),
    enabled: canManageOrg && !!orgId,
  })

  // Filter out global types that have been cloned
  const clonedFromIds = new Set(
    (orgTypes as OperationTypeRow[]).map((t) => t.clonedFromId).filter(Boolean)
  )
  const deleteMutation = useMutation<{ success: boolean; deleted?: boolean; disabled?: boolean }, unknown, { id: string; hardDelete: boolean }>({
    mutationFn: ({ id, hardDelete }) => deleteOperationType({ data: { id, hardDelete } }),
    onSuccess: (result) => {
      if (result.deleted) {
        toast.success('Operation type deleted')
      } else {
        toast.success('Operation type disabled')
      }
      queryClient.invalidateQueries({ queryKey: ['operationTypes'] })
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to delete')
    },
  })

  const cloneMutation = useMutation({
    mutationFn: (id: string) => cloneOperationType({ data: { id } }),
    onSuccess: () => {
      toast.success('Operation type cloned to your organization')
      queryClient.invalidateQueries({ queryKey: ['operationTypes'] })
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to clone')
    },
  })

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const results = await Promise.allSettled(
        ids.map((id) => {
          const opType = (orgTypes as OperationTypeRow[]).find((t) => t.id === id)
          const hardDelete = opType?._count?.operations === 0
          return deleteOperationType({ data: { id, hardDelete } })
        })
      )
      const failed = results.filter((r) => r.status === 'rejected').length
      if (failed > 0) throw new Error(`Failed to delete ${failed} of ${ids.length} items`)
      return results
    },
    onSuccess: (_, ids) => {
      toast.success(`${ids.length} operation type(s) processed`)
      setSelectedItems([])
      setShowBulkDeleteConfirm(false)
      queryClient.invalidateQueries({ queryKey: ['operationTypes'] })
    },
    onError: () => {
      toast.error('Bulk operation failed')
    },
  })

  const toggleEnabledMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      updateOperationType({ data: { id, enabled } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operationTypes'] })
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to update status')
    },
  })


  const orgColumns: ColumnConfig<OperationTypeRow>[] = [
    {
      key: 'name',
      label: 'Name',
      primary: true,
      sortable: false,
      render: (opType) => {
        const isDeleted = !!opType.deletedAt
        return (
          <div className="flex flex-col">
            <span className={isDeleted ? 'font-medium text-muted-foreground line-through' : 'font-medium'}>
              {opType.labels?.en || opType.name}
            </span>
            {opType.clonedFrom && (
              <span className="text-xs text-muted-foreground">
                Cloned from: {opType.clonedFrom.labels?.en || opType.clonedFrom.name}
              </span>
            )}
          </div>
        )
      },
    },
    {
      key: 'version',
      label: 'Version',
      width: 'w-20',
      render: (opType) => (
        <Badge variant="outline" className="text-xs">
          v{opType.version}
        </Badge>
      ),
    },
    {
      key: 'fields',
      label: 'Fields',
      width: 'w-20',
      render: (opType) => (
        <span className="text-muted-foreground">
          {opType.formSchema?.fields?.length || 0}
        </span>
      ),
    },
    {
      key: 'usage',
      label: 'Usage',
      width: 'w-24',
      render: (opType) => (
        <span className="text-xs text-muted-foreground">
          {opType._count?.operations || 0} operations
        </span>
      ),
    },
    {
      key: 'enabled',
      label: t('table.status'),
      width: 'w-24',
      align: 'right',
      render: (opType) => {
        if (opType.deletedAt) {
          return <Badge variant="destructive" className="text-xs">Deleted</Badge>
        }
        return (
          <EnabledAction
            enabled={opType.enabled}
            ariaLabel={opType.enabled ? 'Enabled' : 'Disabled'}
            onToggle={async (enabled) => {
              await toggleEnabledMutation.mutateAsync({ id: opType.id, enabled })
            }}
          />
        )
      },
    },
  ]

  const globalColumns: ColumnConfig<OperationTypeRow>[] = [
    {
      key: 'name',
      label: 'Name',
      primary: true,
      sortable: false,
      render: (opType) => (
        <div className="flex flex-col">
          <span className="font-medium">
            {opType.labels?.en || opType.name}
            {opType.labels?.en ? (
              <span className="text-xs text-muted-foreground font-mono ml-1">
                ({opType.name})
              </span>
            ) : null}
          </span>
        </div>
      ),
    },
    {
      key: 'version',
      label: 'Version',
      width: 'w-20',
      render: (opType) => (
        <Badge variant="outline" className="text-xs">
          v{opType.version}
        </Badge>
      ),
    },
    {
      key: 'fields',
      label: 'Fields',
      width: 'w-20',
      render: (opType) => (
        <span className="text-muted-foreground">
          {opType.formSchema?.fields?.length || 0}
        </span>
      ),
    },
  ]

  if (isUserLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner />
      </div>
    )
  }

  if (!canManageOrg || !orgId) {
    return (
      <div>
        <AdminToolbar>
          <AdminToolbar.Left>
            <AdminToolbar.Title className="text-xl font-semibold">Operation Types</AdminToolbar.Title>
          </AdminToolbar.Left>
        </AdminToolbar>
        <div className="container py-8 text-muted-foreground">
          {!orgId
            ? 'You are not a member of any organization.'
            : 'You do not have permission to manage operation types.'}
        </div>
      </div>
    )
  }

  const isLoading = isOrgTypesLoading || isGlobalTypesLoading

  return (
    <div className="bg-background text-foreground space-y-8">
      <AdminToolbar selectedCount={selectedItems.length}>
          <AdminToolbar.BulkActions>
            <Button
              variant="destructive"
              size="lg"
              onClick={() => setShowBulkDeleteConfirm(true)}
              disabled={bulkDeleteMutation.isPending}
            >
              <TrashIcon className="w-4 h-4 mr-2" />
              Delete Selected
            </Button>
          </AdminToolbar.BulkActions>

          <AdminToolbar.Left>
            <div className="flex gap-1">
              <ToolbarTabLink to="/manage/operations" active={false}>
                <ListIcon className="h-4 w-4" />
                {t('orgPage.title')}
              </ToolbarTabLink>
              <ToolbarTabLink to="/manage/organization/operation-types" active={true}>
                <WrenchIcon className="h-4 w-4" />
                {t('operationTypes')}
              </ToolbarTabLink>
            </div>
            <CreateAction
              to="/manage/organization/operation-types/new"
              label="Create Type"
              size="sm"
            />
          </AdminToolbar.Left>
      </AdminToolbar>

      <section>
        <div className="mb-3">
          <h2 className="text-lg font-semibold">Global Types</h2>
          <p className="text-sm text-muted-foreground">Clone global types into your organization.</p>
        </div>

        <div className="mt-4">
          <DataTable<OperationTypeRow>
            data={globalTypes as OperationTypeRow[]}
            columns={globalColumns}
            isLoading={isLoading}
            emptyMessage="No global types available"
            selectable={false}
            actions={(opType) => (
              <>
                <ViewAction
                  to={`/manage/organization/operation-types/${opType.id}`}
                />
                <CloneAction
                  onClone={() => cloneMutation.mutateAsync(opType.id)}
                  isPending={cloneMutation.isPending}
                  cloned={clonedFromIds.has(opType.id)}
                />
              </>
            )}
          />
        </div>
      </section>

      <section>
        <div className="mb-3">
          <h2 className="text-lg font-semibold">Organization Types</h2>
          <p className="text-sm text-muted-foreground">View and edit your organization-specific operation types.</p>
        </div>

        <div className="mt-4">
          <DataTable<OperationTypeRow>
            data={orgTypes as OperationTypeRow[]}
            columns={orgColumns}
            isLoading={isLoading}
            emptyMessage="No organization types yet. Create one or clone from global types above."
            selectable={true}
            selectedItems={selectedItems}
            onSelectionChange={setSelectedItems}
            actions={(opType) => (
              <>
                <ViewAction
                  to={`/manage/organization/operation-types/${opType.id}`}
                />
                {!opType.deletedAt && (
                  <>
                    <EditAction
                      to={`/manage/organization/operation-types/${opType.id}/edit`}
                    />
                    <DeleteAction
                      itemName={opType.labels?.en || opType.name}
                      itemType="operation type"
                      onDelete={async () => {
                        const hardDelete = (opType._count?.operations || 0) === 0
                        await deleteMutation.mutateAsync({ id: opType.id, hardDelete })
                      }}
                      isPending={deleteMutation.isPending}
                    />
                  </>
                )}
              </>
            )}
          />
        </div>
      </section>

      {/* Bulk Delete Confirmation */}
      <ConfirmModal
        open={showBulkDeleteConfirm}
        onOpenChange={setShowBulkDeleteConfirm}
        title={`Delete ${selectedItems.length} operation type(s)?`}
        description="This will delete or disable the selected operation types depending on whether they have operations attached. Soft-deleted types can be re-enabled later."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="destructive"
        onConfirm={() => bulkDeleteMutation.mutate(selectedItems)}
        isLoading={bulkDeleteMutation.isPending}
      />
    </div>
  )
}
